import type { GetStaticPropsContext } from 'next';

type Messages = Record<string, any>;
type Locale = 'en' | 'uk' | 'pl';

const DEFAULT_LOCALE: Locale = 'en';

// ========= helpers =========
function isPlainObject(v: any): v is Messages {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

// base = en, override = locale (locale перемагає, en заповнює пропуски)
function deepMerge(base: Messages, override: Messages): Messages {
  const result: Messages = isPlainObject(base) ? { ...base } : {};
  if (!isPlainObject(override)) return result;

  for (const key of Object.keys(override)) {
    const b = result[key];
    const o = override[key];

    if (isPlainObject(b) && isPlainObject(o)) result[key] = deepMerge(b, o);
    else result[key] = o;
  }

  return result;
}

function normalizeLocale(locale?: string | null): Locale {
  if (!locale) return DEFAULT_LOCALE;
  const base = locale.toLowerCase().split('-')[0];
  return base === 'uk' || base === 'pl' ? base : 'en';
}

// ========= static imports (стабільно для webpack) =========
async function importBaseMessages(locale: Locale): Promise<Messages> {
  if (locale === 'uk') return (await import('../messages/uk.json')).default;
  if (locale === 'pl') return (await import('../messages/pl.json')).default;
  return (await import('../messages/en.json')).default;
}

async function importGuidesMessages(locale: Locale): Promise<Messages> {
  if (locale === 'uk')
    return (await import('../messages/guides/uk.json')).default;
  if (locale === 'pl') return {};
  return (await import('../messages/guides/en.json')).default;
}

async function importIntegrationsMessages(locale: Locale): Promise<Messages> {
  if (locale === 'uk')
    return (await import('../messages/integrations/uk.json')).default;
  if (locale === 'pl') return {};
  return (await import('../messages/integrations/en.json')).default;
}

async function importBusinessUseCasesMessages(
  locale: Locale
): Promise<Messages> {
  if (locale === 'uk')
    return (await import('../messages/program-promotion/uk.json')).default;
  if (locale === 'pl') return {};
  return (await import('../messages/program-promotion/en.json')).default;
}

async function importShopifyStandaloneMessages(
  locale: Locale
): Promise<Messages> {
  if (locale === 'uk')
    return (await import('../messages/shopify-standalone/uk.json')).default;
  if (locale === 'pl')
    return (await import('../messages/shopify-standalone/pl.json')).default;
  return (await import('../messages/shopify-standalone/en.json')).default;
}

async function importShopifyGuidesMessages(locale: Locale): Promise<Messages> {
  if (locale === 'uk')
    return (await import('../messages/shopify-guides/uk.json')).default;
  if (locale === 'pl')
    return (await import('../messages/shopify-guides/pl.json')).default;
  return (await import('../messages/shopify-guides/en.json')).default;
}

// ========= caching =========
const cache = new Map<string, Messages>();

export function isGuidesPath(pathname?: string) {
  // підлаштуй під свої реальні роутинги гайдів
  return Boolean(
    pathname &&
      (pathname.includes('/how-it-work') ||
        pathname.includes('/how-it-works') ||
        pathname.includes('/dashboard/how-it-work') ||
        pathname.includes('/dashboard/how-it-works'))
  );
}

export async function loadMessages(
  localeInput: string,
  opts?: {
    includeGuides?: boolean;
    includeIntegrations?: boolean;
    includeBusinessUseCases?: boolean;
    includeShopifyStandalone?: boolean;
    includeShopifyGuides?: boolean;
  }
): Promise<Messages> {
  const locale = normalizeLocale(localeInput);
  const includeGuides = Boolean(opts?.includeGuides);
  const includeIntegrations = Boolean(opts?.includeIntegrations);
  const includeBusinessUseCases = Boolean(opts?.includeBusinessUseCases);
  const includeShopifyStandalone = Boolean(opts?.includeShopifyStandalone);
  const includeShopifyGuides = Boolean(opts?.includeShopifyGuides);

  const cacheKey = `${locale}|guides:${
    includeGuides ? '1' : '0'
  }|integrations:${includeIntegrations ? '1' : '0'}|businessUseCases:${
    includeBusinessUseCases ? '1' : '0'
  }|shopifyStandalone:${includeShopifyStandalone ? '1' : '0'}|shopifyGuides:${
    includeShopifyGuides ? '1' : '0'
  }`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;

  // base: en + locale
  const baseEn = await importBaseMessages('en');
  const baseLoc = locale === 'en' ? {} : await importBaseMessages(locale);
  const base = deepMerge(baseEn, baseLoc);

  if (
    !includeGuides &&
    !includeIntegrations &&
    !includeBusinessUseCases &&
    !includeShopifyStandalone &&
    !includeShopifyGuides
  ) {
    cache.set(cacheKey, base);
    return base;
  }

  let merged = base;

  if (includeGuides) {
    const guidesEn = await importGuidesMessages('en');
    const guidesLoc = locale === 'en' ? {} : await importGuidesMessages(locale);
    const guides = deepMerge(guidesEn, guidesLoc);
    merged = deepMerge(merged, guides);
  }

  if (includeIntegrations) {
    const integrationsEn = await importIntegrationsMessages('en');
    const integrationsLoc =
      locale === 'en' ? {} : await importIntegrationsMessages(locale);
    const integrations = deepMerge(integrationsEn, integrationsLoc);
    merged = deepMerge(merged, integrations);
  }

  if (includeBusinessUseCases) {
    const businessUseCasesEn = await importBusinessUseCasesMessages('en');
    const businessUseCasesLoc =
      locale === 'en' ? {} : await importBusinessUseCasesMessages(locale);
    const businessUseCases = deepMerge(businessUseCasesEn, businessUseCasesLoc);
    merged = deepMerge(merged, businessUseCases);
  }

  if (includeShopifyStandalone) {
    const standaloneEn = await importShopifyStandaloneMessages('en');
    const standaloneLoc =
      locale === 'en' ? {} : await importShopifyStandaloneMessages(locale);
    merged = deepMerge(merged, {
      shopifyStandalone: deepMerge(standaloneEn, standaloneLoc)
    });
  }

  if (includeShopifyGuides) {
    const guidesEn = await importShopifyGuidesMessages('en');
    const guidesLoc =
      locale === 'en' ? {} : await importShopifyGuidesMessages(locale);
    merged = deepMerge(merged, {
      shopifyGuides: deepMerge(guidesEn, guidesLoc)
    });
  }

  cache.set(cacheKey, merged);
  return merged;
}

// ========= getStaticProps helpers =========
export async function getI18nProps(ctx: GetStaticPropsContext) {
  const locale = (ctx.locale || DEFAULT_LOCALE) as string;
  const messages = await loadMessages(locale, { includeGuides: false });
  return { props: { messages } };
}

export async function getGuidesI18nProps(ctx: GetStaticPropsContext) {
  const locale = (ctx.locale || DEFAULT_LOCALE) as string;
  const messages = await loadMessages(locale, {
    includeGuides: true,
    includeIntegrations: false
  });
  return { props: { messages } };
}

export async function getIntegrationsI18nProps(ctx: GetStaticPropsContext) {
  const locale = (ctx.locale || DEFAULT_LOCALE) as string;
  const messages = await loadMessages(locale, {
    includeGuides: false,
    includeIntegrations: true
  });
  return { props: { messages } };
}

export async function getBusinessUseCasesI18nProps(ctx: GetStaticPropsContext) {
  const locale = (ctx.locale || DEFAULT_LOCALE) as string;
  const messages = await loadMessages(locale, {
    includeGuides: false,
    includeIntegrations: false,
    includeBusinessUseCases: true
  });
  return { props: { messages } };
}

type SeoMeta = {
  title?: string;
  description?: string;
  keywords?: string;
  url: string;
};

export async function getI18nPropsWithSeo(
  ctx: GetStaticPropsContext,
  namespace: string,
  url: string
) {
  const { props } = await getI18nProps(ctx);
  const messages = props.messages as Messages;
  const seo =
    isPlainObject(messages[namespace]) && isPlainObject(messages[namespace].seo)
      ? messages[namespace].seo
      : {};

  return {
    props: {
      ...props,
      seoMeta: {
        ...(typeof seo.title === 'string' ? { title: seo.title } : {}),
        ...(typeof seo.description === 'string'
          ? { description: seo.description }
          : {}),
        ...(typeof seo.keywords === 'string' ? { keywords: seo.keywords } : {}),
        url
      } satisfies SeoMeta
    }
  };
}
